import { atom } from 'recoil';
import { recoilPersist } from 'recoil-persist';
import { SupportedLanguage, getLanguageOverrideFromSearch } from 'i18n/types';
import {
  ArsCustomerInfo,
  LoginResponse,
  MembershipResponse,
  ShopDetailResponse,
  ShopPublic,
} from 'types/api';

export const loadingState = atom<boolean>({
  key: 'loadingState',
  default: true,
});

export const isMenuOpenState = atom<boolean>({
  key: 'isMenuOpenState',
  default: false,
});

export const isLoginFormOpenState = atom<boolean>({
  key: 'isLoginFormOpenState',
  default: false,
});

const { persistAtom } = recoilPersist({
  key: 'kaddieState',
  storage: localStorage,
});

/**
 * card
 */
export const shopState = atom<ShopPublic | null>({
  key: 'shopState',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

export const cardListState = atom<[]>({
  key: 'cardListState',
  default: [],
  effects_UNSTABLE: [persistAtom],
});

/**
 * shop
 */

export const shopDetailState = atom<ShopDetailResponse | null>({
  key: 'shopDetailState',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

/**
 * i18n - URL의 lang 파라미터로 매장 설정 언어를 덮어쓰기 위한 상태
 * 진입 URL(?lang=en 등)을 초기값으로 읽고, 이후 라우팅 변경 시 갱신됨
 */
export const langOverrideState = atom<SupportedLanguage | null>({
  key: 'langOverrideState',
  default: getLanguageOverrideFromSearch(window.location.search),
});

/**
 * auth
 */
export const customerState = atom<LoginResponse | null>({
  key: 'customerState',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

export const isAuthenticatedState = atom<boolean>({
  key: 'isAuthenticatedState',
  default: false,
  effects_UNSTABLE: [persistAtom],
});

/**
 * membership
 */
export const selectedMembershipState = atom<MembershipResponse | null>({
  key: 'selectedMembershipState',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

/**
 * booking
 */
export const selectedBookingState = atom<any | null>({
  key: 'selectedBookingState',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

export const entrySourceState = atom<any | null>({
  key: 'entrySourceState',
  default: '',
});

/**
 * 예약 모드
 * - callgate: shopId 파라미터로 진입한 국내(콜게이트) 매장. 홀수×인원 기반 시간 점유 모델 사용
 * - global: shopName으로 진입한 글로벌 매장. 기존 세션 기반 모델 사용
 */
export type BookingMode = 'callgate' | 'global';

export const bookingModeState = atom<BookingMode>({
  key: 'bookingModeState',
  default: 'global',
  effects_UNSTABLE: [persistAtom],
});

/**
 * user(국내 접속의 경우에만 필요)
 */
export const arsCustomerInfoState = atom<ArsCustomerInfo | null>({
  key: 'arsCustomerInfoState',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

/**
 * 전화 진입 링크의 utm_source (ARS: autocall_book, 통화매니저 부재중 문자: kt_call_manager)
 * 링크로 들어올 때 읽어 두고, 앱 안에서 이동해 URL에서 빠져도 유지한다.
 * 예약 채널(bookingChannelCd)과 퍼널 로그 data.channel에 쓴다.
 *
 * persistAtom을 쓰지 않는다 — 첫 진입(매장 바뀜, kaddieState 삭제) 경로에서 onSet 저장이 빠져
 * 새로고침하면 값이 사라졌다. 진입 시 saveUtmSource로 직접 저장하고, 페이지 로드 때 기본값으로 읽는다.
 */
const UTM_SOURCE_STORAGE_KEY = 'kaddieUtmSource';

const readStoredUtmSource = (): string | null => {
  try {
    return localStorage.getItem(UTM_SOURCE_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

export const saveUtmSource = (utmSource: string | null) => {
  try {
    if (utmSource) localStorage.setItem(UTM_SOURCE_STORAGE_KEY, utmSource);
    else localStorage.removeItem(UTM_SOURCE_STORAGE_KEY);
  } catch {
    // 저장 실패 시 이번 방문(메모리) 값만 유지한다
  }
};

export const utmSourceState = atom<string | null>({
  key: 'utmSourceState',
  default: readStoredUtmSource(),
});
